import React, { createContext, useContext, useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import currencyUtils, { formatAmount } from '../utils/currencyUtils';
import { validateClientId as validateClientIdUtil } from '../utils/validationClient';
export const GlobalContext = createContext();

export const useGlobalContext = () => {
  return useContext(GlobalContext);
};

export const GlobalProvider = ({ children }) => {
  const { t } = useTranslation();

  const [store, setStore] = useState(() => {
    const savedStore = sessionStorage.getItem('store');
    return savedStore ? JSON.parse(savedStore) : {};
  });
  const [professionalUser, setProfessionalUser] = useState(null);
  const [customParams, setCustomParams] = useState(() => {
    const savedParams = sessionStorage.getItem('customParams');
    return savedParams ? JSON.parse(savedParams) : {};
  });

  const [loading, setLoading] = useState(!sessionStorage.getItem('store'));
  const [error, setError] = useState(null);
  const [selectedLang, setSelectedLang] = useState(() =>
    sessionStorage.getItem('lang') || 'en');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  const [currencyPosition, setCurrencyPosition] = useState('11');
  const [currencyData, setCurrencyData] = useState({});
  const [languages, setLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState(null);
  const [defaultLanguage, setDefaultLanguage] = useState(null);
  const [user, setUser] = useState({ clientId: null });
  const [applyDiscount, setApplyDiscount] = useState(false);  // L'état de la remise
  const deferredSteps = useRef([]);
  const isTrackingInitialized = useRef(false);
  const [selectedStore, setSelectedStore] = useState(() => {
    return sessionStorage.getItem('selectedStore') || '';
  });

  const [cookieAccepted, setCookieAccepted] = useState(() => {
    const saved = localStorage.getItem('cookieAccepted');
    return saved === 'true';
  });

  const [step, setStep] = useState(() => {
    const saved = sessionStorage.getItem('step');
    return saved ? Number(saved) : 1;
  });

  const [trackingAdded, setTrackingAdded] = useState({
    google_tag_manager: false,
    google_global_site: false,
    google_analytics: false,
    facebook_pixel: false
  });
  const initializeCurrencyData = useCallback(async () => {
    try {
      const data = await currencyUtils.loadCurrencyData();
      setCurrencyData(data);
      return data;
    } catch (error) {
      console.error('Error loading currency data:', error);
      return {};
    }
  }, []);
  const initializeProfessionalUser = useMemo(() => {
    return () => {
      if (!store.b2b2c) {
        setProfessionalUser(null);
        sessionStorage.removeItem('professionalUser');
        sessionStorage.removeItem('d');
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const encodedData = urlParams.get('d');

      if (!encodedData || encodedData === 'null') {
        setProfessionalUser(null);
        sessionStorage.removeItem('professionalUser');
        sessionStorage.removeItem('d');
        return;
      }

      try {
        const decodedData = JSON.parse(atob(encodedData));
        const {
          lastname,
          firstname,
          email,
          client,
          discount,
          language,
          callback,
          key,
          limit_card,
          limit_amount
        } = decodedData;

        if (lastname && firstname && email && client &&
          discount && language && callback && key) {
          const professionalUserData = {
            lastname,
            firstname,
            email,
            confEmail: email,
            client,
            discount,
            limit_card,
            limit_amount,
            callback,
            key,
            language
          };

          setProfessionalUser(professionalUserData);
          sessionStorage.setItem('professionalUser', JSON.stringify(professionalUserData));
          sessionStorage.setItem('d', encodedData);
        }
      } catch (error) {
        console.error('Error decoding professional user data:', error);
        setProfessionalUser(null);
        sessionStorage.removeItem('professionalUser');
        sessionStorage.removeItem('d');
      }
    };
  }, [store.b2b2c]);
  const addTracking = useCallback((step) => {
    if (!store.trackings) return;

    if (store.trackings.google_tag_manager &&
      !trackingAdded.google_tag_manager &&
      (!store.trackings.google_global_site || trackingAdded.google_global_site) &&
      store.trackings.google_tag_manager.steps.includes(step)) {

      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${store.trackings.google_tag_manager.data}`;
      document.head.appendChild(gtmScript);

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });

      setTrackingAdded(prev => ({ ...prev, google_tag_manager: true }));
    }

    if (store.trackings.google_global_site &&
      !trackingAdded.google_global_site &&
      (!store.trackings.google_tag_manager || trackingAdded.google_tag_manager) &&
      store.trackings.google_global_site.steps.includes(step)) {

      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;  // Make gtag globally available

      gtag('js', new Date());
      gtag('config', store.trackings.google_global_site.data);

      const ggsScript = document.createElement('script');
      ggsScript.async = true;
      ggsScript.src = `https://www.googletagmanager.com/gtag/js?id=${store.trackings.google_global_site.data}`;
      document.head.appendChild(ggsScript);

      setTrackingAdded(prev => ({ ...prev, google_global_site: true }));
    }

    if (store.trackings.google_analytics &&
      !trackingAdded.google_analytics &&
      store.trackings.google_analytics.steps.includes(step)) {

      window.ga = window.ga || function () {
        (window.ga.q = window.ga.q || []).push(arguments);
      };
      window.ga.l = +new Date();

      window.ga('create', store.trackings.google_analytics.data, 'auto');
      window.ga('send', 'pageview');

      const gaScript = document.createElement('script');
      gaScript.async = true;
      gaScript.src = 'https://www.google-analytics.com/analytics.js';
      document.head.appendChild(gaScript);

      setTrackingAdded(prev => ({ ...prev, google_analytics: true }));
    }

    if (store.trackings.facebook_pixel &&
      !trackingAdded.facebook_pixel &&
      store.trackings.facebook_pixel.steps.includes(step)) {

      window.fbq = window.fbq || function () {
        window.fbq.callMethod ?
          window.fbq.callMethod.apply(window.fbq, arguments) :
          window.fbq.queue.push(arguments);
      };

      if (!window._fbq) window._fbq = window.fbq;
      window.fbq.push = window.fbq;
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
      window.fbq.queue = [];

      const fbScript = document.createElement('script');
      fbScript.async = true;
      fbScript.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(fbScript);

      window.fbq('init', store.trackings.facebook_pixel.data);
      window.fbq('track', 'PageView');

      setTrackingAdded(prev => ({ ...prev, facebook_pixel: true }));
    }
  }, [store.trackings, trackingAdded]);

  const prepareTracking = useCallback((step) => {
    if (store.showCookieAcceptance) {
      if (cookieAccepted) {
        addTracking(step);
      } else if (!isTrackingInitialized.current) {
        deferredSteps.current.push(step);
        const listener = () => {
          if (cookieAccepted) {
            deferredSteps.current.forEach(s => addTracking(s));
            deferredSteps.current = [];
            isTrackingInitialized.current = true;
            window.removeEventListener('cookieAccepted', listener);
          }
        };
        window.addEventListener('cookieAccepted', listener);
      }
    } else {
      addTracking(step);
    }
  }, [cookieAccepted, store.showCookieAcceptance, addTracking]);

  const validateClientId = (value) => {
    return validateClientIdUtil(value, store.clientIdType, store.isClientIdOptional);
  };

  useEffect(() => {
    if (store.discount?.eligibility === 2 && user?.clientId) {
      const isValid = validateClientId(user.clientId);
      setApplyDiscount(isValid);
    } else if (store.discount?.eligibility === 0) {
      setApplyDiscount(true);
    }
  }, [store.discount, user?.clientId, validateClientId]);

  function getBaseUrl() {
    const fullUrl = window.location.href;
    const url = new URL(fullUrl);
    return url.origin;
  }

  const fetchStoreData = useMemo(() => async () => {
    try {
      const baseURL = process.env.REACT_APP_ENV == "localhost" ? process.env.REACT_APP_BASE_URL : getBaseUrl();
      //  const baseURL = process.env.REACT_APP_BASE_URL;
      const graphics = process.env.REACT_APP_GRAPHICS_API_URL;

      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
      const apiUrl = `${graphics}/api/signboard/graphics?url=${baseURL}&mobile=${isMobile ? '1' : '0'}&api_key=PC69DQS479Y4E7M79C69Y4E7M`;

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Erreur API : ${response.statusText}`);
      }

      const data = await response.json();
      if (data.status === 'ko' || data.isAvailable === false) {
        const storeData = {
          ...data.result,
          exception: data.errors,
          isAvailable: false
        };
        setStore(storeData);
        throw new Error(data.errors[0]?.message || 'Une erreur est survenue');
      }

      const storeData = {
        ...data.result,
        allowAmex: data.allowAmex,
        panier: data.panier,
        b2b2c: data.b2b2c,
        isAvailable: data.isAvailable,
        errors: [],
        cssUrls: [data.result.css, data.result.user_css].filter(Boolean)
      };

      setStore(storeData);
      initializeLanguage(storeData);
      setGlobalData(data.result);
      setCustomParams(data.result);
      initializeProfessionalUser();

      sessionStorage.setItem('store', JSON.stringify(storeData));
      sessionStorage.setItem('customParams', JSON.stringify(data.result));

    } catch (err) {
      setStore(prevStore => ({
        ...prevStore,
        errors: [{ message: t('Le service est en maintenance et momentanément interrompu. Veuillez nous excuser pour le désagrément occasionné, nous travaillons à sa remise en ligne dans les plus brefs délais. Nous vous invitons à réessayer ultérieurement.') }],
        isAvailable: false
      }));
      setError(err.message || 'Une erreur est survenue lors du chargement des données.');
    } finally {
      setLoading(false);
    }
  }, [initializeProfessionalUser]);

  const initializeLanguage = (storeData) => {
    if (storeData.languages && storeData.languages.length > 0) {
      setLanguages(storeData.languages);
      const savedLang = sessionStorage.getItem('lang');
      const defaultLang = storeData.default_language;

      let langToUse;

      if (savedLang) {
        langToUse = storeData.languages.find(l => l.code === savedLang);
      }

      if (!langToUse) {
        langToUse = storeData.languages.find(l => l.code === defaultLang) || storeData.languages[0];
      }
      i18n.changeLanguage(langToUse.code);
      setDefaultLanguage(defaultLang);
      setCurrentLanguage(langToUse);
      setCurrencyPosition(langToUse.currencyPosition || '11');

      sessionStorage.setItem('lang', langToUse.code);
    }
  };
  useEffect(() => {
    fetchStoreData();
  }, [fetchStoreData]);
  useEffect(() => {
    initializeCurrencyData();
  }, [initializeCurrencyData]);
  useEffect(() => {
    initializeProfessionalUser();
  }, [initializeProfessionalUser, window.location.search]);
  useEffect(() => {
    prepareTracking(step);
  }, [step, prepareTracking]);
  const contextValue = useMemo(() => ({
    store,
    customParams,
    loading,
    error,
    step,
    selectedLang,
    modalVisible,
    modalContent,
    globalData,
    cookieAccepted,
    currentLanguage,
    languages,
    defaultLanguage,
    prepareTracking,
    addTracking,
    cookieAccepted,
    setCookieAccepted,
    validateClientId,
    setUser,
    currencyData,
    user,
    applyDiscount,
    selectedStore,
    professionalUser,

    changeLanguage: (langCode) => {
      const newLang = languages.find(l => l.code === langCode);
      if (newLang) {
        setCurrentLanguage(newLang);
        setCurrencyPosition(newLang.currencyPosition || '01');
        sessionStorage.setItem('lang', langCode);
      }
      setSelectedLang(langCode);
    },
    goToStep: (targetStep) => {
      setStep(targetStep);
      sessionStorage.setItem('step', targetStep);
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant"
      });
    },
    setCurrentStore: (s) => {
      setSelectedStore(s);
      sessionStorage.setItem('selectedStore', s);
    },
    showModal: (content) => {
      setModalContent(content);
      setModalVisible(true);
    },
    hideModal: () => {
      setModalVisible(false);
      setModalContent(null);
    },

    formatAmounts: (amount, decimals = 0) => {
      const storeWithCurrency = {
        ...store,
        currencyData,
        devise: store.devise || 'EUR'
      };
      return formatAmount(amount, decimals, storeWithCurrency, currentLanguage);
    },
    acceptCookie: () => {
      setCookieAccepted(true);
      localStorage.setItem('cookieAccepted', 'true');

      const event = new Event('cookieAccepted');
      window.dispatchEvent(event);
    },

    refuseCookie: () => {
      setCookieAccepted(false);
      localStorage.setItem('cookieAccepted', 'false');
    },

    updateProfessionalUser: (updates) => {
      if (professionalUser) {
        const updatedUser = { ...professionalUser, ...updates };
        setProfessionalUser(updatedUser);
        sessionStorage.setItem('professionalUser', JSON.stringify(updatedUser));
      }
    },
    clearProfessionalUser: () => {
      setProfessionalUser(null);
      sessionStorage.removeItem('professionalUser');
      sessionStorage.removeItem('d');
    }
  }), [store, customParams, loading, error, step, selectedLang, modalVisible, user,
    modalContent, globalData, cookieAccepted, currencyPosition, professionalUser, currencyData]);

  return (
    <GlobalContext.Provider value={contextValue}>
      {loading && !sessionStorage.getItem('store') ? (
        <div id="preloader">
          <div id="loader"></div>
          {error && <div className="loading-error">Error: {error}</div>}
        </div>
      ) : (
          children
        )}
    </GlobalContext.Provider>
  );
};